import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core";
import { GetApp, InsertDriveFile } from "@material-ui/icons";

import api from "../../services/api";
import chatBackgroundMask from "../../assets/background.svg";

const useStyles = makeStyles(theme => ({
  previewPane: {
    minHeight: 143,
    padding: theme.spacing(1),
    borderRadius: 6,
    backgroundColor: "#efeae2",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-start",
    overflow: "hidden",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
      maskImage: `url(${chatBackgroundMask})`,
      maskRepeat: "repeat",
      maskMode: "alpha",
      maskSize: "540px 960px",
      WebkitMaskImage: `url(${chatBackgroundMask})`,
      WebkitMaskRepeat: "repeat",
      WebkitMaskSize: "540px 960px",
    },
  },
  previewBubble: {
    width: "100%",
    maxWidth: 330,
    minHeight: 42,
    padding: 5,
    paddingBottom: 18,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    backgroundColor: "#dcf8c6",
    color: "#303030",
    boxShadow: "0 1px 1px #b3b3b3",
    position: "relative",
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    zIndex: 1,
  },
  previewMedia: {
    display: "block",
    width: "100%",
    maxHeight: 150,
    objectFit: "cover",
    borderRadius: 8,
    backgroundColor: "#d8d8d8",
  },
  previewCaption: {
    display: "block",
    padding: "5px 4px 0",
    fontSize: 13,
    lineHeight: "18px",
  },
  previewEmpty: {
    color: "#6f6f6f",
    fontSize: 12,
    lineHeight: "17px",
  },
  previewDocumentShell: {
    borderRadius: 8,
    marginBottom: 2,
    overflow: "hidden",
  },
  previewDocumentFrame: {
    height: 127,
    width: "calc(100% + 20px)",
    border: 0,
    display: "block",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    pointerEvents: "none",
    marginLeft: -2,
    marginTop: -2,
    marginRight: -18,
  },
  previewDocumentCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    color: "#111b21",
    textDecoration: "none",
    overflow: "hidden",
    backgroundColor: "rgba(255, 255, 255, 0.24)",
  },
  previewDocumentIconWrap: {
    width: 34,
    height: 42,
    flex: "0 0 34px",
    borderRadius: 6,
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "inset 0 -1px 0 rgba(0, 0, 0, 0.14)",
  },
  previewDocumentIcon: {
    fontSize: 22,
    lineHeight: 1,
  },
  previewDocumentExtension: {
    maxWidth: 30,
    marginTop: -2,
    fontSize: 8,
    lineHeight: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  previewDocumentInfo: {
    minWidth: 0,
    flex: "1 1 auto",
  },
  previewDocumentName: {
    display: "block",
    fontSize: 14,
    lineHeight: "18px",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  previewDocumentMeta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 3,
    color: "#667781",
    fontSize: 11,
    lineHeight: "14px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  previewDocumentDownloadIcon: {
    flex: "0 0 auto",
    color: "#667781",
    fontSize: 22,
  },
  previewAudio: {
    width: "100%",
    minWidth: 0,
    height: 36,
  },
  previewTime: {
    position: "absolute",
    right: 6,
    bottom: 3,
    fontSize: 10,
    color: "#7a7a7a",
  },
}));

const getSavedMediaUrl = media => {
  if (!media) return "";
  if (media.url || media.mediaUrl) return media.url || media.mediaUrl;
  if (!media.filename) return "";

  const baseUrl = String(api.defaults.baseURL || window.location.origin).replace(/\/+$/, "");
  return `${baseUrl}/public/${encodeURI(media.filename)}`;
};

const getMimeTypeFromName = name => {
  const extension = String(name || "").split(".").pop()?.toLowerCase();

  if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(extension)) {
    return "image/*";
  }
  if (["mp4", "webm", "mov", "m4v", "avi"].includes(extension)) {
    return "video/*";
  }
  if (["mp3", "ogg", "wav", "m4a", "opus"].includes(extension)) {
    return "audio/*";
  }

  return "";
};

const getBlockMediaInfo = block => {
  const file = block?.file || null;
  const media = block?.media || null;
  const name = file?.name || media?.originalName || media?.filename || "";
  const mimetype = file?.type || media?.mimetype || getMimeTypeFromName(name);

  return {
    file,
    name,
    mimetype,
    savedUrl: getSavedMediaUrl(media),
  };
};

const getDocumentExtension = name => {
  const cleanName = String(name || "").split("?")[0];
  const extension = cleanName.includes(".") ? cleanName.split(".").pop() : "";

  return extension ? extension.toUpperCase().slice(0, 5) : "FILE";
};

const getDocumentIconColor = extension => {
  const normalizedExtension = String(extension || "").toLowerCase();

  if (normalizedExtension === "pdf") return "#f15c5c";
  if (["doc", "docx"].includes(normalizedExtension)) return "#4285f4";
  if (["xls", "xlsx", "csv"].includes(normalizedExtension)) return "#21a366";
  if (["ppt", "pptx"].includes(normalizedExtension)) return "#d24726";
  if (["zip", "rar", "7z"].includes(normalizedExtension)) return "#7b61ff";

  return "#667781";
};

const isPdfDocumentPreview = ({ name, mimetype, mediaUrl }) =>
  /\.pdf(\?|$)/i.test(name || "") ||
  /\.pdf(\?|$)/i.test(mediaUrl || "") ||
  /pdf/i.test(mimetype || "");

const renderDocumentPreview = ({ classes, name, mimetype, mediaUrl }) => {
  const filename = name || "Archivo adjunto";
  const extension = getDocumentExtension(filename);
  const showPreview = mediaUrl && isPdfDocumentPreview({ name: filename, mimetype, mediaUrl });

  return (
    <div className={classes.previewDocumentShell}>
      {showPreview && (
        <iframe
          title={filename}
          className={classes.previewDocumentFrame}
          src={mediaUrl}
        />
      )}
      <div className={classes.previewDocumentCard}>
        <span
          className={classes.previewDocumentIconWrap}
          style={{ backgroundColor: getDocumentIconColor(extension) }}
        >
          <InsertDriveFile className={classes.previewDocumentIcon} />
          <span className={classes.previewDocumentExtension}>{extension}</span>
        </span>
        <span className={classes.previewDocumentInfo}>
          <span className={classes.previewDocumentName}>{filename}</span>
          <span className={classes.previewDocumentMeta}>{extension}</span>
        </span>
        <GetApp className={classes.previewDocumentDownloadIcon} />
      </div>
    </div>
  );
};

const MessageBubblePreview = ({ block, emptyText = "Vista previa del mensaje" }) => {
  const classes = useStyles();
  const [fileUrl, setFileUrl] = useState("");
  const { file, name, mimetype, savedUrl } = getBlockMediaInfo(block);
  const mediaUrl = fileUrl || savedUrl;
  const text = block?.text || "";
  const hasMedia = Boolean(file || savedUrl);
  const isImage = mimetype.startsWith("image/");
  const isVideo = mimetype.startsWith("video/");
  const isAudio = mimetype.startsWith("audio/");

  useEffect(() => {
    if (!file) {
      setFileUrl("");
      return undefined;
    }

    const nextUrl = URL.createObjectURL(file);
    setFileUrl(nextUrl);

    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);

  return (
    <div className={classes.previewPane}>
      <div className={classes.previewBubble}>
        {hasMedia ? (
          isImage ? (
            <img className={classes.previewMedia} src={mediaUrl} alt={name || "Vista previa"} />
          ) : isVideo ? (
            <video className={classes.previewMedia} src={mediaUrl} controls muted />
          ) : isAudio ? (
            <audio className={classes.previewAudio} src={mediaUrl} controls />
          ) : (
            renderDocumentPreview({ classes, name, mimetype, mediaUrl })
          )
        ) : null}
        {text ? (
          <span className={classes.previewCaption}>{text}</span>
        ) : !hasMedia ? (
          <span className={classes.previewEmpty}>{emptyText}</span>
        ) : null}
        <span className={classes.previewTime}>ahora</span>
      </div>
    </div>
  );
};

export default MessageBubblePreview;
